/**
 * One row in the closed-deal stage history: a single `lead.activities` item.
 *
 * Renders stage title, last-updated time, status, description, and stage-specific actions
 * (application form, allotment/agreement documents, invoices, management approve/reject, loan approval).
 * Document ids for template download/upload are resolved from `getDocumentByDealId` when the stage
 * name matches allotment or agreement letter.
 */
import React, { useEffect, useState } from 'react';
import Button from '../../shared/components/ui/Button';
import FormInput from '../../shared/components/ui/FormInput';
import Modal from '../../shared/components/ui/Modal';
import {
  downloadInvoice,
  downloadInvoiceForPaymentStages,
  approveActivity,
  getCloseDealsById,
  downloadDocument,
  uploadDocument,
  downloadSignedDocument,
  getDocumentByDealId,
  getCloseDealLoanDetails,
  approveCloseDealLoanDetails
} from '../services/api/implementation/impl';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { ApplicantModal } from '../components/forms/ApplicantModal';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

/**
 * @param {object} props
 * @param {boolean} props.special When true, timeline head styling (document icon, rose-tint node); use for newest row after sort.
 * @param {boolean} [props.isLast] When true (oldest row in newest-first list), masks the parent timeline spine so it does not continue below this node.
 * @param {boolean} [props.isPipelineNewest] When true, shows a "CURRENT" badge (newest activity by `updated_at`).
 * @param {object} props.data One activity from `lead.activities` (`name`, `status`, `description`, `updated_at`, `id`, `lead_id`, `payment_stage_name`, …).
 * @param {number} props.dealId Closed deal id used for invoice downloads and loan APIs.
 * @param {() => void} props.onSuccess Called after mutations/uploads so the parent list can refetch.
 * @param {object} props.allData Full closed deal object (unit pricing, `total_price`, nested lead, etc.).
 */
const DealCard = ({ special, isLast = false, isPipelineNewest = false, data, dealId, onSuccess, allData }) => {
  const userId = Cookies.get('userId');

  const [showApplicantModal, setShowApplicantModal] = useState(false);

  /** Document row id from `getDocumentByDealId`, matched by stage name for allotment / agreement flows. */
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);

  /** From cookie; Management-only UI for approve/reject on the "Management Approval" stage. */
  const [userVertical, setUserVertical] = useState(Cookies.get('userVertical'));

  /** Unit base price components summed for the Management Approval summary card. */
  const InitialPrice =
    Number(allData?.unit?.extra_charges) +
    Number(allData?.unit?.gst) +
    Number(allData?.unit?.plc_price) +
    Number(allData?.unit?.cost);

  /** Full deal by id (reserved for extended UI; e.g. customer proposal fields may be re-enabled). */
  const { data: closeDealsById } = useQuery({
    queryKey: ['closeDealsById', dealId],
    queryFn: () => getCloseDealsById(dealId),
    enabled: !!dealId
  });

  /** All documents attached to this deal; used to find the correct row id for allotment/agreement template + upload. */
  const { data: getDocumentByDealIdData } = useQuery({
    queryKey: ['getDocumentByDealId', dealId],
    queryFn: () => getDocumentByDealId(dealId),
    enabled: !!dealId
  });

  /** Fetched only on the "Loan Approval" stage to decide whether to show the approve-loan CTA. */
  const { data: loanDetailsData, refetch: refetchLoanDetails } = useQuery({
    queryKey: ['loanDetails', dealId],
    queryFn: () => getCloseDealLoanDetails(dealId),
    enabled: !!dealId && data?.name?.toLowerCase() === 'loan approval'
  });

  useEffect(() => {
    mixpanel.track('Deal Card Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  /**
   * When documents load, map this card's activity `name` to API `document_name` and store the first matching id.
   * Supports both spellings "Aggrement" and "Agreement" for the agreement letter stage.
   */
  useEffect(() => {
    if (getDocumentByDealIdData?.length > 0 && data?.name) {
      const documentName = data.name.toLowerCase();
      let targetName = '';

      if (documentName === 'allotment letter') {
        targetName = 'allotment letter';
      } else if (documentName === 'aggrement letter' || documentName === 'agreement letter') {
        targetName = 'agreement letter';
      }

      if (targetName) {
        const filteredTemplateData = getDocumentByDealIdData.filter(
          (item) => item.document_name?.toLowerCase() === targetName
        );

        if (filteredTemplateData.length > 0) {
          setSelectedDocumentId(filteredTemplateData[0].id);
        }
      }
    }
  }, [getDocumentByDealIdData]);

  /** Compact stamp above the card, e.g. "8 May, 11:24 am" (design: newest-first timeline). */
  function formatTimelineStamp(iso) {
    const raw = iso ?? data?.created_at ?? data?.createdAt;
    if (!raw) return '—';
    const d = new Date(raw);
    const day = d.getDate();
    const mon = d.toLocaleString('en-GB', { month: 'short' });
    let t = d.toLocaleString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true });
    t = t.replace(/(am|pm)/i, (m) => m.toLowerCase());
    return `${day} ${mon}, ${t}`;
  }

  const rawStatus = (data?.status ?? '').toString();
  const statusKey = rawStatus.toLowerCase();
  let statusBadgeLabel = rawStatus || '—';
  if (statusKey === 'completed') statusBadgeLabel = 'Completed';
  else if (statusKey === 'pending' || statusKey === 'sign_pending' || statusKey === 'due') {
    statusBadgeLabel = 'Pending';
  }

  // --- Signed PDF (allotment / agreement after customer sign-off) ---
  const { mutate: handleDownloadSigned } = useMutation({
    mutationFn: async (id) => {
      return await downloadSignedDocument(id);
    },
    onSuccess: (data, variables) => {
      const id = variables;
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `signedCopy_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      onSuccess();
      toast.success('Allotment Letter Downloaded');
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || err?.message || 'Error in downloading allotment letter'
      );
      console.error(
        err?.response?.data?.message || err?.message || 'Error in downloading allotment letter'
      );
    }
  });

  /** Upload handler wired to `FormInput` file-btn for signed allotment PDF. */
  const handleUploadAllotmentLetter = async (id, payload) => {
    try {
      await uploadDocument(id, payload);
      onSuccess();
    } catch (error) {
      console.error('Error uploading allotment letter:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error uploading allotment letter'
      );
    }
  };

  /** Upload handler wired to `FormInput` file-btn for signed agreement PDF. */
  const handleUploadAgreementForm = async (id, payload) => {
    try {
      await uploadDocument(id, payload);
      onSuccess();
    } catch (error) {
      console.error('Error uploading agreement letter:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error uploading agreement letter'
      );
    }
  };

  /** Downloads unsigned / template PDF for a document row id (allotment or agreement "Download" buttons). */
  const { mutate: downloadDochandler } = useMutation({
    mutationFn: async (id) => {
      return await downloadDocument(id);
    },
    onSuccess: (data, variables) => {
      const id = variables;
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `document_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Document Downloaded');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Error in downloading document');
      console.error(
        err?.response?.data?.message || err?.message || 'Error in downloading document'
      );
    }
  });

  const handleClickOnAllotmentLetter = (id) => {
    downloadDochandler(id);
  };

  const handleClikcOnAgreementLetter = (id) => {
    downloadDochandler(id);
  };

  // --- Invoices: deal-level performa vs payment-stage-specific PDF ---
  const { mutate: handleInvoiceStagesDownload } = useMutation({
    mutationFn: async (id) => {
      return await downloadInvoice(id);
    },
    onSuccess: (data, variables) => {
      const id = variables;
      if (!data || data.size === 0) {
        toast.error('Error: File data is empty or invalid.');
        return;
      }
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `performa_invoice_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      onSuccess();
      toast.success('Invoice Downloaded');
    },
    onError: (err) => {
      let message;

      // API sometimes returns error bodies as ArrayBuffer; decode for a readable toast.
      if (err?.response?.data && err?.response?.data instanceof ArrayBuffer) {
        try {
          const decoder = new TextDecoder('utf-8');
          const decoded = decoder.decode(err.response.data);
          if (decoded.includes('Customer details missing, please fill application form')) {
            message = 'Customer details missing, please fill application form';
          } else {
            message = decoded;
          }
        } catch (decodeErr) {
          console.error('Error decoding arraybuffer:', decodeErr);
          message = err?.message || 'Error in downloading invoice';
        }
      } else {
        message = err?.response?.data?.message || err?.message || 'Error in downloading invoice';
      }

      toast.error(message);
    }
  });

  const { mutate: handleInvoicePaymentStagesDownload } = useMutation({
    mutationFn: async ({ id, sourceName }) => {
      return await downloadInvoiceForPaymentStages(id, sourceName);
    },
    onSuccess: (data, variables) => {
      const { id, sourceName } = variables;
      if (!data || data.size === 0) {
        toast.error(
          data?.response?.data?.message || data?.message || 'Error in downloading invoice'
        );
        return;
      }
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `Payment_Stage_${id}_${sourceName}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      onSuccess();
      toast.success('Invoice Downloaded');
    },
    onError: (err) => {
      let message;

      if (err?.response?.data && err?.response?.data instanceof ArrayBuffer) {
        try {
          const decoder = new TextDecoder('utf-8');
          const decoded = decoder.decode(err.response.data);
          if (decoded.includes('Customer details missing, please fill application form')) {
            message = 'Customer details missing, please fill application form';
          } else {
            message = decoded;
          }
        } catch (decodeErr) {
          console.error('Error decoding arraybuffer:', decodeErr);
          message = err?.message || 'Error in downloading invoice';
        }
      } else {
        message = err?.response?.data?.message || err?.message || 'Error in downloading invoice';
      }

      toast.error(message);
    }
  });

  // --- Management Approval: same endpoint for approve and reject, different payload `name` ---
  const { mutate: handleApprove } = useMutation({
    mutationFn: (payload) => approveActivity(data?.id, payload),
    onSuccess: () => {
      toast.success('Approved');
      onSuccess();
    }
  });

  const { mutate: handleReject } = useMutation({
    mutationFn: (payload) => approveActivity(data?.id, payload),
    onSuccess: () => {
      toast.success('Rejected');
      onSuccess();
    }
  });

  /** Completes the activity with a positive management outcome. */
  const handleApproveClick = (leadId) => {
    const payload = {
      name: 'Management Approval',
      lead_id: leadId,
      status: 'Completed'
    };
    handleApprove(payload);
  };

  /** Completes the activity with a denied management outcome. */
  const handleRejectClick = (leadId) => {
    const payload = {
      name: 'Management Denied',
      lead_id: leadId,
      status: 'Completed'
    };
    handleReject(payload);
  };

  /** Marks close-deal loan details as approved (uses loan row id when present, else deal id fallback). */
  const { mutate: approveLoan, isPending: isApprovingLoan } = useMutation({
    mutationFn: ({ loanId }) =>
      approveCloseDealLoanDetails(loanId, {
        status: 'Approved',
        approved: true
      }),
    onSuccess: () => {
      toast.success('Loan Accepted successfully');
      refetchLoanDetails();
      onSuccess();
      mixpanel.track('Loan Approved from Stage', {
        user_id: userId,
        deal_id: dealId,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to approve loan');
      mixpanel.track('Loan Approval Failed from Stage', {
        user_id: userId,
        deal_id: dealId,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  /** Prefer dedicated loan record id from API; fall back to deal id if needed. */
  const handleLoanApprovalClick = () => {
    const loanId = loanDetailsData?.data?.id || dealId;
    approveLoan({ loanId });
  };

  const actionsRowClass = 'flex flex-wrap items-center gap-2 sm:gap-3';

  const currentBadgeClass =
    'inline-flex shrink-0 items-center rounded-full border border-[#9dc4cb] bg-[#d6eef0] px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-[#1e4a52]';
  const statusBadgeClass =
    'inline-flex shrink-0 items-center rounded-full border border-gray-200 bg-gray-100 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-gray-700';

  return (
    <div className="flex w-full max-w-full gap-2.5 sm:gap-3">
      {/* Timeline node (spine drawn in DealsList). Head = rose ring + document; rest = teal + org icon. */}
      <div className="relative z-[1] flex w-11 flex-shrink-0 flex-col items-center self-stretch pt-1">
        <div
          className={`relative z-[2] flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 shadow-sm ${
            special
              ? 'border-rose-200 bg-rose-50 ring-2 ring-rose-100/80'
              : 'border-[#b8dade] bg-[#e8f4f6] ring-1 ring-[#2A565E]/10'
          }`}>
          <img
            src={`/icons/${special ? 'document' : 'organisation'}.svg`}
            alt=""
            className="h-5 w-5 object-contain"
          />
        </div>
        {/* Oldest stage is end of story (newest-first list): hide spine below last node — same idea as ActivityCard. */}
        {isLast ? (
          <div className="relative z-[2] min-h-[12px] w-full flex-1 bg-white" aria-hidden />
        ) : null}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <time
          className="text-xs font-medium text-gray-500"
          dateTime={data?.updated_at ?? data?.created_at ?? data?.createdAt}>
          {formatTimelineStamp(data?.updated_at)}
        </time>

        <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold leading-snug text-[#2A565E]">
                {data?.name}
                {data?.payment_stage_name ? (
                  <span className="font-medium text-[#2A565E]/85">
                    {' '}
                    <span className="text-gray-300" aria-hidden>
                      |
                    </span>{' '}
                    {data?.payment_stage_name}
                  </span>
                ) : null}
              </h3>
              {data?.description ? (
                <p className="mt-1 text-sm leading-relaxed text-gray-500">{data.description}</p>
              ) : null}
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5 sm:pt-0">
              {isPipelineNewest ? <span className={currentBadgeClass}>Current</span> : null}
              <span className={statusBadgeClass}>{statusBadgeLabel}</span>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3 empty:hidden">
          {/* --- Stage-specific actions (mutually exclusive branches by activity name + status) --- */}

          {data?.name === 'Application Form' && (
            <div className={actionsRowClass}>
              <Button onClick={() => setShowApplicantModal(true)}>Open Application Form</Button>
            </div>
          )}

          {/*
            Allotment & Agreement (incl. API typo "Aggrement"): Completed → download signed PDF.
            Pending → download template by document id + upload signed copy. Document id from getDocumentByDealId.
          */}
          {data?.name === 'Allotment Letter' && data?.status === 'Completed' ? (
            <div className={actionsRowClass}>
              <Button onClick={() => handleDownloadSigned(selectedDocumentId)} displayType={''}>
                Download Signed Letter
              </Button>
            </div>
          ) : data?.name === 'Allotment Letter' && data?.status === 'Pending' ? (
            <div className={`${actionsRowClass} items-end`}>
              <Button
                onClick={() => handleClickOnAllotmentLetter(selectedDocumentId)}
                displayType={'primary'}>
                Download
              </Button>
              <FormInput
                type={'file-btn'}
                label={'Upload Signed Document'}
                register_key={'allotment_signed_doc'}
                accept={'.pdf'}
                uploadFunctionId={selectedDocumentId}
                uploadFunction={handleUploadAllotmentLetter}
              />
            </div>
          ) : (data?.name === 'Aggrement Letter' || data?.name === 'Agreement Letter') &&
            data?.status === 'Completed' ? (
            <div className={actionsRowClass}>
              <Button onClick={() => handleDownloadSigned(selectedDocumentId)} displayType={''}>
                Download Signed Letter
              </Button>
            </div>
          ) : (data?.name === 'Aggrement Letter' || data?.name === 'Agreement Letter') &&
            data?.status === 'Pending' ? (
            <div className={`${actionsRowClass} items-end`}>
              <Button
                onClick={() => handleClikcOnAgreementLetter(selectedDocumentId)}
                displayType={'primary'}>
                Download
              </Button>
              <FormInput
                type={'file-btn'}
                label={'Upload Signed Document'}
                register_key={'agreement_signed_doc'}
                accept={'.pdf'}
                uploadFunctionId={selectedDocumentId}
                uploadFunction={handleUploadAgreementForm}
              />
            </div>
          ) : /* Demand note performa — deal-level invoice download */
          data?.name === 'Demand Note (Invoice)' && data?.status === 'Completed' ? (
            <div className={actionsRowClass}>
              <Button onClick={() => handleInvoiceStagesDownload(dealId)} displayType={''}>
                Download
              </Button>
            </div>
          ) : /* Advance invoice — performa download by deal id (no status check on this branch) */
          data?.name === 'Invoice Advance' ? (
            <div className={actionsRowClass}>
              <Button onClick={() => handleInvoiceStagesDownload(dealId)} displayType={''}>
                Download
              </Button>
            </div>
          ) : /* Per-payment-stage invoice PDF (filename includes payment_stage_name) */
          data?.name === 'Payment Stage (Invoice)' ? (
            <div className={actionsRowClass}>
              <Button
                onClick={() =>
                  handleInvoicePaymentStagesDownload({
                    id: dealId,
                    sourceName: data?.payment_stage_name
                  })
                }
                displayType={''}>
                Download
              </Button>
            </div>
          ) : /* Pricing summary + approve/reject (Management vertical only, via cookie) */
          data?.name === 'Management Approval' && data?.status !== 'Completed' ? (
            <div className="flex flex-col gap-3">
              <div className="rounded-lg border border-gray-200/80 bg-white/90 p-3 shadow-sm sm:p-4">
                <div className="flex flex-col gap-2 text-sm text-gray-700 sm:flex-row sm:flex-wrap sm:gap-x-8 sm:gap-y-2">
                  <p>
                    <span className="text-gray-500">Initial price</span>
                    <br />
                    <span className="font-semibold text-gray-900">₹ {InitialPrice ?? 'N/A'}</span>
                  </p>
                  <p>
                    <span className="text-gray-500">Final price</span>
                    <br />
                    <span className="font-semibold text-gray-900">
                      ₹ {allData?.total_price ?? 'N/A'}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  disabled={userVertical !== 'Management'}
                  onClick={() => handleApproveClick(data?.lead_id)}
                  displayType={''}>
                  Approve
                </Button>
                <Button
                  disabled={userVertical !== 'Management'}
                  onClick={() => handleRejectClick(data?.lead_id)}
                  customStyle={{
                    backgroundColor: '#f15d47',
                    color: 'white',
                    borderColor: '#f15d47'
                  }}>
                  Reject
                </Button>
              </div>
            </div>
          ) : /* Loan stage: show approve only while loan row exists and is not yet approved */
          data?.name === 'Loan Approval' &&
            data?.status !== 'Completed' &&
            loanDetailsData?.data &&
            loanDetailsData?.data?.approved !== true ? (
            <div className={actionsRowClass}>
              <Button onClick={handleLoanApprovalClick} disabled={isApprovingLoan} displayType={''}>
                {isApprovingLoan ? 'Accepting...' : 'Loan Approved'}
              </Button>
            </div>
          ) : null}
          </div>
        </div>
      </div>

      {/* Embedded application form for the "Application Form" activity; full page reload on close to refresh activities */}
      {showApplicantModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name="Application Form"
          onClose={() => setShowApplicantModal(false)}
          width={50}>
          <ApplicantModal
            dealId={dealId}
            onClose={() => {
              setShowApplicantModal(false);
              window.location.reload();
            }}
          />
        </Modal>
      )}
    </div>
  );
};

export default DealCard;
